import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { ShimmerSkeleton } from '../../../components/animations/Skeleton';
import { HStack, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

// Mirrors LeadListItem: a glass card with a 42px avatar, a title row carrying a
// stage pill, three icon info-lines, and a footer with a source badge and
// timestamp. Matching the real card's chrome and dimensions keeps the loading
// state from jumping when data resolves.
function LeadListItemSkeleton() {
  return (
    <View style={listItemStyles.card}>
      <HStack spacing="md" align="start">
        <ShimmerSkeleton width={42} height={42} borderRadius={21} />
        <VStack spacing="xs" style={{ flex: 1, minWidth: 0 }}>
          <HStack justify="between" align="center">
            <ShimmerSkeleton width="50%" height={15} borderRadius={4} />
            <ShimmerSkeleton width={80} height={22} borderRadius={999} />
          </HStack>
          <VStack spacing="xs" style={{ marginTop: 2 }}>
            <InfoLineSkeleton width="65%" />
            <InfoLineSkeleton width="45%" />
            <InfoLineSkeleton width="70%" />
          </VStack>
          <HStack justify="between" align="center" style={{ marginTop: 6 }}>
            <ShimmerSkeleton width={22} height={22} borderRadius={11} />
            <ShimmerSkeleton width={64} height={11} borderRadius={4} />
          </HStack>
        </VStack>
      </HStack>
    </View>
  );
}

// Mirrors LeadListItem's card chrome (a plain View + StyleSheet, radius lg,
// border.default). Built the same way as the real row so the loading state
// rounds and bordered identically on native.
const listItemStyles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.default,
    padding: tokens.spacing.md,
    overflow: 'hidden',
  },
});

function InfoLineSkeleton({ width }: Readonly<{ width: string }>) {
  return (
    <HStack spacing="sm" align="center" style={{ paddingVertical: 2 }}>
      <ShimmerSkeleton width={13} height={13} borderRadius={3} />
      <ShimmerSkeleton width={width} height={12} borderRadius={4} />
    </HStack>
  );
}

export function LeadListSkeleton({ count = 8 }: Readonly<{ count?: number }>) {
  return (
    <VStack spacing="sm">
      {Array.from({ length: count }).map((_, i) => {
        const key = `lead-skeleton-${String(i)}`;
        return <LeadListItemSkeleton key={key} />;
      })}
    </VStack>
  );
}

// Mirrors InfoCard chrome from the lead detail screen (surface.row,
// radius 2xl, border.default, subtle shadow). Uses a plain View + StyleSheet
// rather than Box: the Box `borderRadius` prop wasn't rounding the corners on
// native here, leaving flat square rectangles that didn't read as cards the way
// the web build does. `overflow: 'hidden'` guarantees the children clip to the
// rounded corners.
function DetailCardShell({ children }: Readonly<{ children: ReactNode }>) {
  return <View style={shellStyles.card}>{children}</View>;
}

const shellStyles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius['2xl'],
    borderWidth: 1,
    borderColor: colors.border.default,
    padding: tokens.spacing.lg,
    overflow: 'hidden',
    ...(tokens.shadow?.subtle as object),
  },
});

function DetailSectionSkeleton({ lines = 3 }: Readonly<{ lines?: number }>) {
  return (
    <DetailCardShell>
      <VStack spacing="md">
        <HStack spacing="sm" align="center">
          <ShimmerSkeleton width={18} height={18} borderRadius={4} />
          <ShimmerSkeleton width="40%" height={18} borderRadius={4} />
        </HStack>
        {Array.from({ length: lines }).map((_, i) => {
          const key = `detail-line-${String(i)}`;
          return (
            <HStack key={key} spacing="sm" align="center">
              <ShimmerSkeleton width={14} height={14} borderRadius={4} />
              <ShimmerSkeleton
                width={i === lines - 1 ? '50%' : '70%'}
                height={14}
                borderRadius={4}
              />
            </HStack>
          );
        })}
      </VStack>
    </DetailCardShell>
  );
}

export function LeadDetailSkeleton() {
  // No own padding: the detail screen's ScrollView already supplies
  // paddingHorizontal/Top (lg), and the real content uses `spacing="2xl"`.
  // Adding padding here double-insets the skeleton so its cards land further
  // in than the loaded content, which reads as misaligned on load.
  return (
    <VStack spacing="2xl">
      <HStack spacing="md" align="center">
        <ShimmerSkeleton width={32} height={32} borderRadius={8} />
        <VStack spacing="xs" style={{ flex: 1 }}>
          <ShimmerSkeleton width="60%" height={20} borderRadius={4} />
          <ShimmerSkeleton width="35%" height={14} borderRadius={4} />
        </VStack>
      </HStack>

      <DetailCardShell>
        <VStack spacing="md">
          <HStack spacing="md" align="center">
            <ShimmerSkeleton width={48} height={48} borderRadius={24} />
            <VStack spacing="xs" style={{ flex: 1 }}>
              <ShimmerSkeleton width="50%" height={16} borderRadius={4} />
              <ShimmerSkeleton width="70%" height={12} borderRadius={4} />
            </VStack>
          </HStack>
          <ShimmerSkeleton width="100%" height={6} borderRadius={3} />
          <HStack spacing="sm">
            <ShimmerSkeleton width="30%" height={24} borderRadius={12} />
            <ShimmerSkeleton width="25%" height={24} borderRadius={12} />
          </HStack>
        </VStack>
      </DetailCardShell>

      <DetailSectionSkeleton lines={4} />

      <DetailCardShell>
        <VStack spacing="md">
          <HStack spacing="sm" align="center">
            <ShimmerSkeleton width={18} height={18} borderRadius={4} />
            <ShimmerSkeleton width="35%" height={18} borderRadius={4} />
          </HStack>
          <ShimmerSkeleton width="100%" height={60} borderRadius={8} />
          <HStack spacing="sm">
            {Array.from({ length: 4 }).map((_, i) => {
              const key = `agent-skeleton-${String(i)}`;
              return <ShimmerSkeleton key={key} width="22%" height={40} borderRadius={8} />;
            })}
          </HStack>
        </VStack>
      </DetailCardShell>

      <DetailSectionSkeleton lines={5} />

      <DetailSectionSkeleton lines={3} />
    </VStack>
  );
}
